import { useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Link2, Download, MonitorDown, Apple, Server as ServerIcon, Rocket, ExternalLink, KeyRound, Save, Check } from "lucide-react";

// Operator page: generate a shareable installer link for a client. Hand the link
// to the client; anyone who opens it can install the agent on their device with
// one command. Devices come in unassigned and are grouped by client on the
// dashboard, where per-client policy then applies.

function Copyable({ label, value, mono = true, testid }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
      <div className="flex items-start gap-2">
        <code className={`flex-1 bg-zinc-900/40 border border-zinc-900 rounded p-2.5 text-[12px] break-all ${mono ? "font-mono" : ""}`} data-testid={testid}>{value}</code>
        <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}
          className="bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-2.5 py-2 text-zinc-300 shrink-0"><Copy size={14} /></button>
      </div>
    </div>
  );
}

const PLATFORMS = [
  { key: "windows", label: "Windows", icon: MonitorDown, hint: "Win 10 / 11 · Server 2016+" },
  { key: "macos", label: "macOS", icon: Apple, hint: "Apple Silicon · Intel" },
  { key: "linux", label: "Linux", icon: ServerIcon, hint: "Ubuntu · Debian · RHEL" },
];

export default function Installer() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [batch, setBatch] = useState(true);
  const [platform, setPlatform] = useState("windows");
  const [bundle, setBundle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cred, setCred] = useState({ configured: false, username: "", domain: ".", password: "" });
  const [savingCred, setSavingCred] = useState(false);

  useEffect(() => { api.get("/clients").then(r => setClients(asArray(r.data))).catch(() => setClients([])); }, []);

  // load the client's existing deployment credential when a client is chosen
  useEffect(() => {
    if (!clientId) { setCred({ configured: false, username: "", domain: ".", password: "" }); return; }
    api.get(`/clients/${clientId}/deploy-credential`)
      .then(r => setCred({ configured: !!r.data?.configured, username: r.data?.username || "", domain: r.data?.domain || ".", password: "" }))
      .catch(() => setCred({ configured: false, username: "", domain: ".", password: "" }));
  }, [clientId]);

  const saveCred = async () => {
    if (!clientId) { toast.error("Pick a client first — the credential is stored per client"); return; }
    if (!cred.username.trim() || !cred.password.trim()) { toast.error("Username and password required"); return; }
    setSavingCred(true);
    try {
      await api.put(`/clients/${clientId}/deploy-credential`, { username: cred.username, domain: cred.domain || ".", password: cred.password });
      setCred(c => ({ ...c, configured: true, password: "" }));
      toast.success("Admin credential saved (encrypted) — devices under this client will install elevated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setSavingCred(false); }
  };
  const clearCred = async () => {
    if (!clientId || !window.confirm("Remove the deployment credential for this client?")) return;
    try { await api.delete(`/clients/${clientId}/deploy-credential`); setCred({ configured: false, username: "", domain: ".", password: "" }); toast.success("Credential removed"); }
    catch { toast.error("Failed"); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/agents/install-token", { client_id: clientId || undefined, batch });
      setBundle(data);
      toast.success("Installer link generated");
    } catch (e) {
      toast.error("Could not generate installer");
    } finally { setBusy(false); }
  };

  const shareLink = bundle ? `${bundle.server_url}/install?token=${bundle.install_token}` : "";

  return (
    <div className="p-6 md:p-8 max-w-[1000px] mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
          <Rocket className="text-blue-400" size={18} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-50">Agent Installer</h1>
          <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">Generate a link to give out</div>
        </div>
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Client (optional)</div>
            <select value={clientId} onChange={e => setClientId(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200">
              <option value="">Unassigned (group later)</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="text-[11px] text-zinc-600 mt-1.5">Devices enrol under this client and inherit its policy.</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Link type</div>
            <div className="flex gap-2">
              <button onClick={() => setBatch(true)} className={`flex-1 rounded px-3 py-2 text-sm border ${batch ? "bg-blue-500/15 border-blue-500/40 text-blue-300" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}>Reusable (30d)</button>
              <button onClick={() => setBatch(false)} className={`flex-1 rounded px-3 py-2 text-sm border ${!batch ? "bg-blue-500/15 border-blue-500/40 text-blue-300" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}>Single-use (7d)</button>
            </div>
            <div className="text-[11px] text-zinc-600 mt-1.5">Reusable = one link for a whole company rollout.</div>
          </div>
          <div className="flex items-end">
            <button onClick={generate} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 text-blue-200 rounded-md px-4 py-2 text-sm disabled:opacity-50">
              <Link2 size={15} /> {busy ? "Generating…" : "Generate link"}
            </button>
          </div>
        </div>

        {/* Local admin credential — so devices enrolling under this client install
            elevated (no UAC prompt). Stored encrypted; used by silent deploys,
            software bundles and offboarding. */}
        <div className="mt-4 pt-4 border-t border-zinc-900">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={14} className="text-zinc-500" />
            <div className="text-sm text-zinc-200">Local admin credential <span className="text-zinc-600 font-normal">(optional)</span></div>
            {cred.configured && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400/90 ml-1"><Check size={12} /> configured{cred.username ? ` · ${cred.username}` : ""}</span>}
          </div>
          {!clientId ? (
            <div className="text-[12px] text-zinc-500">Pick a client above to attach an admin account — the credential is stored per client, and every device that enrols under it installs elevated.</div>
          ) : (
            <>
              <div className="text-[12px] text-zinc-500 mb-3">The local-admin account you already know for this client's machines. Devices enrolling under <span className="text-zinc-300">{clients.find(c => c.id === clientId)?.name}</span> will run installs, software bundles and offboarding elevated — no UAC prompt.</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Username</div>
                  <input className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 outline-none focus:border-zinc-600" placeholder="Administrator" value={cred.username} onChange={e => setCred(s => ({ ...s, username: e.target.value }))} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Domain (. for local)</div>
                  <input className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 outline-none focus:border-zinc-600" value={cred.domain} onChange={e => setCred(s => ({ ...s, domain: e.target.value }))} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Password{cred.configured ? " (enter to replace)" : ""}</div>
                  <input type="password" className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 outline-none focus:border-zinc-600" placeholder="••••••••" value={cred.password} onChange={e => setCred(s => ({ ...s, password: e.target.value }))} />
                </div>
              </div>
              <div className="flex items-start gap-2 text-[11px] text-amber-500/80 mt-2">
                <KeyRound size={13} className="shrink-0 mt-0.5" />
                <span>Prefer the built-in Administrator or a dedicated service account — a normal UAC-filtered admin returns a reduced token, so machine-wide installs to Program Files/HKLM can still be denied. For full reliability, install the agent as the SYSTEM service.</span>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={saveCred} disabled={savingCred}
                  className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded px-3.5 py-2 text-sm text-zinc-200 disabled:opacity-50"><Save size={14} /> {savingCred ? "Saving…" : "Save credential"}</button>
                {cred.configured && <button onClick={clearCred} className="text-sm text-red-400/80 hover:text-red-300">Remove</button>}
              </div>
            </>
          )}
        </div>
      </div>

      {bundle && (
        <div className="mt-4 space-y-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/25 rounded-md p-5">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="text-sm font-medium text-zinc-100">Shareable installer page</div>
              <div className="flex items-center gap-2 text-[11px] font-mono">
                <span className="text-zinc-500">{bundle.batch ? "reusable" : "single-use"} · expires in {bundle.expires_days}d</span>
                {bundle.company && <span className="text-blue-300">· {bundle.company}</span>}
              </div>
            </div>
            <Copyable label="Give this link to the client" value={shareLink} testid="share-link" />
            <a href={shareLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-300 text-xs mt-2 hover:underline">
              <ExternalLink size={13} /> Open the installer page
            </a>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="flex items-center gap-1.5 mb-4">
              {PLATFORMS.map(p => {
                const Icon = p.icon;
                return (
                  <button key={p.key} onClick={() => setPlatform(p.key)}
                    className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm border ${platform === p.key ? "bg-zinc-800 border-zinc-700 text-zinc-100" : "bg-zinc-900/40 border-zinc-900 text-zinc-400"}`}>
                    <Icon size={14} /> {p.label}
                  </button>
                );
              })}
            </div>
            <div className="space-y-3">
              <Copyable label={`One-line install (${platform})`} value={bundle.install_commands?.[platform]} testid="install-cmd" />
              <div className="flex flex-wrap gap-2 pt-1">
                <a href={bundle.downloads?.windows} className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-3 py-1.5 text-xs text-zinc-300"><Download size={13} /> Windows .exe</a>
                <a href={bundle.downloads?.macos_arm64} className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-3 py-1.5 text-xs text-zinc-300"><Download size={13} /> macOS (Apple)</a>
                <a href={bundle.downloads?.macos_amd64} className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-3 py-1.5 text-xs text-zinc-300"><Download size={13} /> macOS (Intel)</a>
                <a href={bundle.downloads?.linux} className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-3 py-1.5 text-xs text-zinc-300"><Download size={13} /> Linux</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
